import { useState, useEffect, useRef, useCallback, useMemo, useDeferredValue } from "react";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash/debounce";
import { fetchAPI } from "../../config/api";
import { getErrorMessage } from "../../utils/errorUtils";
import { getBaseStateOrder } from "../../config/expectedRunsColumns";
import { DEFAULT_YEAR, DEFAULT_DIVISION } from "../../config/constants";
import { ENDPOINTS, COLUMN_MAP } from "./constants";

export const useGutsData = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [dataType, setDataType] = useState(searchParams.get("dataType") || "guts");
  const [selectedYears, setSelectedYears] = useState([DEFAULT_YEAR]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [division, setDivision] = useState(Number(searchParams.get("division")) || DEFAULT_DIVISION);
  const [conference, setConference] = useState(searchParams.get("conference") || "");
  const [conferences, setConferences] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showError, setShowError] = useState(false);

  const deferredSearch = useDeferredValue(searchTerm);
  const dataAbortRef = useRef(null);
  const conferencesAbortRef = useRef(null);
  const mounted = useRef(false);

  const updateParamsRef = useRef(null);
  if (!updateParamsRef.current) {
    updateParamsRef.current = debounce((state) => {
      const params = new URLSearchParams();
      params.set("dataType", state.dataType);
      params.set("division", state.division.toString());
      params.set("search", state.searchTerm);
      if (state.conference) params.set("conference", state.conference);
      setSearchParams(params);
    }, 300);
  }

  const buildApiParams = useCallback(() => {
    const params = new URLSearchParams();

    if (dataType === "park_factors") {
      params.set("division", division.toString());
      if (conference) params.set("conference", conference);
    } else if (dataType === "expected_runs") {
      params.set("division", division.toString());
      params.set("year", selectedYears[0]?.toString() || DEFAULT_YEAR.toString());
    } else if (dataType === "guts" && selectedYears[0]) {
      params.set("year", selectedYears[0].toString());
    }

    return params;
  }, [dataType, division, conference, selectedYears]);

  const fetchData = useCallback(async () => {
    dataAbortRef.current?.abort();
    const controller = new AbortController();
    dataAbortRef.current = controller;

    setLoading(true);
    setError(null);
    setShowError(false);

    try {
      const params = buildApiParams();
      const endpoint = ENDPOINTS[dataType] || ENDPOINTS.guts;
      const url = params.toString() ? `${endpoint}?${params}` : endpoint;

      const result = await fetchAPI(url, { signal: controller.signal });
      const rows = Array.isArray(result) ? result : [];

      if (!controller.signal.aborted) {
        setData(rows);
        setError(null);
        setShowError(false);
        setIsInitialized(true);
      }
    } catch (err) {
      if (err.name !== "AbortError" && !controller.signal.aborted) {
        setError(getErrorMessage(err, { division, dataType }));
        setShowError(true);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [dataType, buildApiParams, division]);

  useEffect(() => {
    if (dataType === "park_factors" && division) {
      conferencesAbortRef.current?.abort();
      const controller = new AbortController();
      conferencesAbortRef.current = controller;

      fetchAPI(`/api/conferences?division=${division}`, { signal: controller.signal })
        .then((response) => {
          if (!controller.signal.aborted && Array.isArray(response)) {
            setConferences(response);
          }
        })
        .catch((err) => {
          if (err.name !== "AbortError") setConferences([]);
        });
    } else {
      setConferences([]);
    }
  }, [dataType, division]);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    updateParamsRef.current({ dataType, division, searchTerm, conference });
  }, [dataType, division, searchTerm, conference]);

  useEffect(() => {
    if (!mounted.current) return;
    fetchData();
  }, [dataType, division, selectedYears, conference, fetchData]);

  useEffect(() => () => {
    dataAbortRef.current?.abort();
    conferencesAbortRef.current?.abort();
    updateParamsRef.current?.cancel();
  }, []);

  const columns = useMemo(() => {
    const getColumns = COLUMN_MAP[dataType] || COLUMN_MAP.guts;
    return getColumns();
  }, [dataType]);

  const filteredData = useMemo(() => {
    let rows = data;

    if (deferredSearch) {
      const search = deferredSearch.toLowerCase();
      rows = rows.filter((item) => {
        if (!item || typeof item !== "object") return false;
        const teamName = (item.team_name || item.team || item.name || "").toLowerCase();
        const year = (item.year || item.Year || "").toString();
        return teamName.includes(search) || year.includes(search);
      });
    }

    let cleaned = rows.filter((item) => item && typeof item === "object");

    if (dataType === "expected_runs") {
      cleaned = [...cleaned].sort(
        (a, b) => getBaseStateOrder(a.bases) - getBaseStateOrder(b.bases)
      );
    }

    return cleaned;
  }, [data, deferredSearch, dataType]);

  return {
    dataType, setDataType,
    selectedYears, setSelectedYears,
    searchTerm, setSearchTerm,
    division, setDivision,
    conference, setConference,
    conferences,
    columns,
    filteredData,
    loading,
    error,
    showError,
    isInitialized,
    fetchData,
  };
};
